import React, { useState, useEffect } from 'react';
import {
    Paper, Box, Typography, Chip, CircularProgress, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
    Send as SendIcon, Edit as EditIcon, FileDownload as FileDownloadIcon,
    Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import invoicingService from '../../services/invoicing';
import { getInvoiceStatusChipColor } from '../../constants/statusColors';
import { PrimaryButton, SecondaryButton, FilledDestructiveButton } from '../common/Buttons';
import PaymentDialog from './PaymentDialog';
import VoidDialog from './VoidDialog';
import SendDialog from './SendDialog';

const InvoicePanel = ({ invoiceId, onUpdate }) => {
    const navigate = useNavigate();
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sendingInvoice, setSendingInvoice] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [sendDialogOpen, setSendDialogOpen] = useState(false);
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [voidDialogOpen, setVoidDialogOpen] = useState(false);
    const [recalcLoading, setRecalcLoading] = useState(false);

    useEffect(() => {
        if (invoiceId) {
            fetchInvoice();
        }
    }, [invoiceId]);

    const fetchInvoice = async () => {
        try {
            setLoading(true);
            const response = await invoicingService.getInvoice(invoiceId);
            setInvoice(response.data);
        } catch (error) {
            console.error('Failed to fetch invoice:', error);
            toast.error('Failed to load invoice');
        } finally {
            setLoading(false);
        }
    };

    const handleSendInvoice = async () => {
        try {
            setSendingInvoice(true);
            await invoicingService.sendInvoice(invoice.id);
            toast.success('Invoice sent successfully');
            await fetchInvoice();
        } catch (error) {
            const msg = error.response?.data?.detail || 'Failed to send invoice';
            toast.error(msg);
        } finally {
            setSendingInvoice(false);
        }
    };

    const handleRecalculateRates = async () => {
        setRecalcLoading(true);
        try {
            await invoicingService.recalculateRates(invoice.id);
            toast.success('Line items recalculated with latest rate cards');
            await fetchInvoice();
            if (onUpdate) onUpdate();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to recalculate rates');
        } finally {
            setRecalcLoading(false);
        }
    };

    const handlePreviewPDF = async () => {
        try {
            const response = await invoicingService.previewPDF(invoice.id, true);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${invoice.invoice_number}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            toast.error('Failed to preview PDF');
        }
    };

    const handleDirectDownloadPDF = async () => {
        try {
            const response = await invoicingService.downloadPDF(invoice.id);
            if (response.data && response.data.url) {
                window.open(response.data.url, '_blank');
            } else {
                toast.error('No download URL returned from server');
            }
        } catch (error) {
            const msg = error.response?.data?.error || error.message || 'Failed to download PDF';
            toast.error(msg);
        }
    };

    const handlePaymentSuccess = async (remainingBalance) => {
        setPaymentDialogOpen(false);
        await fetchInvoice();
        if (onUpdate) onUpdate();
        toast.success('Payment recorded successfully');
    };

    const handleVoidSuccess = async () => {
        setVoidDialogOpen(false);
        await fetchInvoice();
        if (onUpdate) onUpdate();
        toast.success('Invoice voided successfully');
    };

    if (loading) {
        return (
            <Paper sx={{ p: 3, borderLeft: '4px solid', borderColor: 'primary.main', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 100 }}>
                    <CircularProgress size={24} />
                </Box>
            </Paper>
        );
    }

    if (!invoice) {
        return null;
    }

    return (
        <>
            <Paper sx={{ p: 3, borderLeft: '4px solid', borderColor: 'primary.main', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Invoice {invoice.invoice_number}
                        </Typography>
                        <Chip
                            label={invoice.status_display}
                            color={getInvoiceStatusChipColor(invoice.status)}
                            size="small"
                        />
                    </Box>
                    <SecondaryButton size="small" onClick={() => navigate(`/invoicing/${invoice.id}`)}>
                        View full invoice →
                    </SecondaryButton>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2 }}>
                    <Box>
                        <Typography variant="caption" color="textSecondary">Due Date</Typography>
                        <Typography variant="body2">{invoice.due_date}</Typography>
                    </Box>
                    {invoice.status === 'VOID' ? (
                        <>
                            <Box>
                                <Typography variant="caption" color="textSecondary">Invoiced Amount</Typography>
                                <Typography variant="body2">₹{parseFloat(invoice.total_amount).toFixed(2)}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="textSecondary">Amount Paid</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    ₹{parseFloat(invoice.amount_paid || 0).toFixed(2)}
                                </Typography>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Box>
                                <Typography variant="caption" color="textSecondary">Total Amount</Typography>
                                <Typography variant="body2">₹{parseFloat(invoice.total_amount).toFixed(2)}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="textSecondary">Balance Due</Typography>
                                <Typography variant="body2" sx={{ color: invoice.balance_due > 0 ? 'error.main' : 'success.main', fontWeight: 'bold' }}>
                                    ₹{parseFloat(invoice.balance_due).toFixed(2)}
                                </Typography>
                            </Box>
                        </>
                    )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {invoice.status === 'DRAFT' && (
                        <>
                            <SecondaryButton size="small" startIcon={<EditIcon />} onClick={() => navigate(`/invoicing/${invoice.id}/edit`)}>
                                Edit Invoice
                            </SecondaryButton>
                            <SecondaryButton
                                size="small"
                                onClick={handleRecalculateRates}
                                disabled={recalcLoading}
                            >
                                {recalcLoading ? <CircularProgress size={18} color="inherit" /> : 'Refresh Rates'}
                            </SecondaryButton>
                            <PrimaryButton size="small" startIcon={<SendIcon />} onClick={() => setSendDialogOpen(true)}>
                                Send Invoice
                            </PrimaryButton>
                        </>
                    )}
                    {invoice.status === 'PAID' && (
                        <FilledDestructiveButton size="small" onClick={() => setVoidDialogOpen(true)}>Void</FilledDestructiveButton>
                    )}
                    {['SENT', 'PARTIAL', 'OVERDUE'].includes(invoice.status) && (
                        <>
                            <SecondaryButton
                                size="small"
                                onClick={async () => {
                                    setResendLoading(true);
                                    try {
                                        await invoicingService.resendInvoice(invoice.id);
                                        toast.success('Invoice resent');
                                        await fetchInvoice();
                                    } catch {
                                        toast.error('Failed to resend invoice');
                                    } finally {
                                        setResendLoading(false);
                                    }
                                }}
                                disabled={resendLoading}
                            >
                                {resendLoading ? <CircularProgress size={20} color="inherit" /> : 'Resend'}
                            </SecondaryButton>
                            <FilledDestructiveButton size="small" onClick={() => setVoidDialogOpen(true)}>Void</FilledDestructiveButton>
                        </>
                    )}

                    {/* PDF button */}
                    {invoice.status === 'DRAFT' ? (
                        <SecondaryButton size="small" startIcon={<FileDownloadIcon />} onClick={handlePreviewPDF}>
                            Preview PDF
                        </SecondaryButton>
                    ) : (
                        <SecondaryButton size="small" startIcon={<FileDownloadIcon />} onClick={handleDirectDownloadPDF}>
                            Download PDF
                        </SecondaryButton>
                    )}
                </Box>
            </Paper>

            <PaymentDialog
                open={paymentDialogOpen}
                invoiceId={invoice.id}
                balanceDue={invoice.balance_due}
                onClose={() => setPaymentDialogOpen(false)}
                onSuccess={handlePaymentSuccess}
            />

            <VoidDialog
                open={voidDialogOpen}
                invoiceId={invoice.id}
                onClose={() => setVoidDialogOpen(false)}
                onSuccess={handleVoidSuccess}
            />

            <SendDialog
                open={sendDialogOpen}
                invoiceId={invoice.id}
                onClose={() => setSendDialogOpen(false)}
                onSuccess={() => { setSendDialogOpen(false); fetchInvoice(); }}
            />
        </>
    );
};

export default InvoicePanel;
